import { api } from "./api";

// Interface para o avatar do usuário salvo no Funifier Studio
export interface UserAvatarData {
  _id: string;
  userId: string;
  avatarName: string;
  avatarImageUrl: string;
  avatarCategories: {
    [category: string]: {
      itemId: string | null;
      itemName: string | null;
      itemImageUrl: string | null;
      position: { x: number; y: number } | null;
      scale: number | null;
      rotation: number | null;
      color: string | null;
    };
  };
  created: { $date: number };
  updated: { $date: number };
}

// Configuração da API para buscar avatares salvos
const AVATAR_API_CONFIG = {
  baseURL: 'https://service2.funifier.com/v3',
  endpoint: '/database/user_avatar__c',
  token: 'Basic NjRjNjgzYmNjMjdiZmYxNDM5Yjc0ZTk1OjYzYzliNjc2ZDMxODI0MDdkZGVmZGIyYw=='
};

/**
 * Busca o avatar mais recente do usuário na collection user_avatar__c
 * @param userId ID do usuário para buscar o avatar
 * @returns Promise com os dados do avatar ou null se não encontrado
 */
export async function fetchUserAvatar(userId: string): Promise<UserAvatarData | null> {
  try {
    const response = await fetch(
      `${AVATAR_API_CONFIG.baseURL}${AVATAR_API_CONFIG.endpoint}?q=userId:'${userId}'`,
      {
        method: 'GET',
        headers: {
          'Authorization': AVATAR_API_CONFIG.token,
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache'
        }
      }
    );

    if (!response.ok) {
      return null;
    }

    const avatars: UserAvatarData[] = await response.json();
    
    if (!Array.isArray(avatars) || avatars.length === 0) {
      return null;
    }

    const latestAvatar = avatars.sort((a, b) => 
      new Date(b.updated.$date).getTime() - new Date(a.updated.$date).getTime()
    )[0];

    return latestAvatar;

  } catch (error) {
    return null;
  }
}

/**
 * Busca o avatar do usuário usando a API padrão da aplicação (fallback)
 * Esta função usa a API já configurada da aplicação
 */
export async function fetchUserAvatarWithAPI(userId: string): Promise<UserAvatarData | null> {
  try {
    const { data } = await api.get(`database/user_avatar__c?q=userId:'${userId}'`);
    
    if (!Array.isArray(data) || data.length === 0) {
      return null;
    }

    const latestAvatar = data.sort((a: UserAvatarData, b: UserAvatarData) => 
      new Date(b.updated.$date).getTime() - new Date(a.updated.$date).getTime()
    )[0];

    return latestAvatar;

  } catch (error) {
    return null;
  }
}

/**
 * Busca avatar simulado no localStorage (apenas para testes)
 */
function getSimulatedAvatar(userId: string): UserAvatarData | null {
  try {
    const key = `mock_avatar_${userId}`;
    const mockData = localStorage.getItem(key);
    
    if (mockData) {
      const avatar = JSON.parse(mockData);
      return avatar;
    }
  } catch (error) {
  }
  
  return null;
}

/**
 * Função principal para buscar o avatar do usuário
 * Tenta primeiro avatares simulados (testes), depois API direta, depois API padrão
 */
export async function getUserAvatar(userId: string): Promise<UserAvatarData | null> {
  try {
    const simulatedAvatar = getSimulatedAvatar(userId);
    if (simulatedAvatar) {
      return simulatedAvatar;
    }
    
    let avatar = await fetchUserAvatar(userId);
    
    if (!avatar) {
      avatar = await fetchUserAvatarWithAPI(userId);
    }
    
    return avatar;
    
  } catch (error) {
    return null;
  }
}

/**
 * Obtém apenas a URL da imagem do avatar do usuário
 */
export async function getUserAvatarImageUrl(userId: string): Promise<string | null> {
  try {
    const avatar = await getUserAvatar(userId);
    return avatar?.avatarImageUrl || null;
  } catch (error) {
    return null;
  }
}
